import {
  Box,
  Tabs,
  Tab,
  Typography,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Grid2,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Stack
} from '@mui/material';
import MainCard from 'components/MainCard';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import { SyntheticEvent, useState, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import RequiredIndicator from 'components/@extended/RequiredIndicator';
import useAuth from 'hooks/useAuth';
import { getAllSubjects } from 'services/subject.service';
import { getAllDivision } from 'services/division.service';
import { getFilterListUser } from 'services/user.service';
import Snackbar from 'utils/Snackbar';
import { subjectTimeoff, teacherTimeoff, timeoffSubject, timeoffTeacher } from 'services/workload.service';
import { divisionTimeoff, timeoffDivision } from 'services/division.service';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`timeoff-tabpanel-${index}`} aria-labelledby={`timeoff-tab-${index}`} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}
function a11yProps(index: number) {
  return {
    id: `timeoff-tab-${index}`,
    'aria-controls': `timeoff-tabpanel-${index}`
  };
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const LECTURES_COUNT = 6;

interface ScheduleGridProps {
  schedule: boolean[][];
  onToggle: (lecture: number, day: number) => void;
  lecturesCount: number;
  daysCount: number;
  dayLabels: string[];
  disabled?: boolean;
  loading?: boolean;
}

function ScheduleGrid({ schedule, onToggle, lecturesCount, daysCount, dayLabels, disabled = false, loading = false }: ScheduleGridProps) {
  return (
    <TableContainer component={Paper} elevation={3} sx={{ mt: 4, position: 'relative' }}>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Lecture</TableCell>
            {dayLabels.map((day) => (
              <TableCell key={day} align="center">
                <strong>{day.slice(0, 3)}</strong>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: lecturesCount }, (_, i) => i + 1).map((lectureNum) => (
            <TableRow key={lectureNum}>
              <TableCell>Lecture {lectureNum}</TableCell>
              {Array.from({ length: daysCount }, (_, dayIndex) => (
                <TableCell key={dayIndex} align="center">
                  <Checkbox
                    checked={schedule[lectureNum - 1]?.[dayIndex] || false}
                    onChange={() => onToggle(lectureNum - 1, dayIndex)}
                    disabled={disabled || loading}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function TimeOffManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user }: any = useAuth();
  const organizationId = user?.organizationId?._id ?? null;

  const [tabValue, setTabValue] = useState(0);

  // Loading states
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [loadingDivisions, setLoadingDivisions] = useState(true);
  const [loadingTeachers, setLoadingTeachers] = useState(true);

  // Time-off loading states
  const [loadingSubjectTimeoff, setLoadingSubjectTimeoff] = useState(false);
  const [loadingDivisionTimeoff, setLoadingDivisionTimeoff] = useState(false);
  const [loadingTeacherTimeoff, setLoadingTeacherTimeoff] = useState(false);

  const [lecturesCount, setLecturesCount] = useState(6);
  const [daysCount, setDaysCount] = useState(6);
  const [dayLabels, setDayLabels] = useState(DAYS);

  // Dropdown options
  const [subjectOptions, setSubjectOptions] = useState<{ value: string; label: string }[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<{ value: string; label: string }[]>([]);
  const [teacherOptions, setTeacherOptions] = useState<{ value: string; label: string }[]>([]);
  const [academicYearOptions, setAcademicYearOptions] = useState<{ value: string; label: string }[]>([]);

  // Selected values
  const [academicYear, setAcademicYear] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedDivision, setSelectedDivision] = useState<string>('');
  const [selectedTeacher, setSelectedTeacher] = useState<string>('');

  const [subjectSchedule, setSubjectSchedule] = useState<boolean[][]>([]);
  const [divisionSchedule, setDivisionSchedule] = useState<boolean[][]>([]);
  const [teacherSchedule, setTeacherSchedule] = useState<boolean[][]>([]);

  useEffect(() => {
    const emptySchedule = Array(lecturesCount)
      .fill(null)
      .map(() => Array(daysCount).fill(false));

    setSubjectSchedule(emptySchedule);
    setDivisionSchedule(emptySchedule);
    setTeacherSchedule(emptySchedule);
  }, [lecturesCount, daysCount]);

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSubjectToggle = (lecture: number, day: number) => {
    setSubjectSchedule((prev) => {
      const newSch = prev.map((row) => [...row]);
      newSch[lecture][day] = !newSch[lecture][day];
      return newSch;
    });
  };

  const handleDivisionToggle = (lecture: number, day: number) => {
    setDivisionSchedule((prev) => {
      const newSch = prev.map((row) => [...row]);
      newSch[lecture][day] = !newSch[lecture][day];
      return newSch;
    });
  };

  const handleTeacherToggle = (lecture: number, day: number) => {
    setTeacherSchedule((prev) => {
      const newSch = prev.map((row) => [...row]);
      newSch[lecture][day] = !newSch[lecture][day];
      return newSch;
    });
  };

  const applyScheduleFromApi = (apiResponse: any, lecturesCount: number, daysCount: number): boolean[][] => {
    const schedule = Array.from({ length: lecturesCount }, () => Array(daysCount).fill(false));

    let timeOffEntries: any[] = [];

    const result = apiResponse?.result || apiResponse;

    // 1. Try common direct paths
    if (Array.isArray(result?.timeOff)) {
      timeOffEntries = result.timeOff;
    }
    // 2. Subject/Teacher style (nested in array[0])
    else if (Array.isArray(result?.subjectTimeOff?.[0]?.timeOff)) {
      timeOffEntries = result.subjectTimeOff[0].timeOff;
    } else if (Array.isArray(result?.teacherTimeOff?.[0]?.timeOff)) {
      timeOffEntries = result.teacherTimeOff[0].timeOff;
    }
    // 3. Division style – MOST IMPORTANT FOR YOUR CASE
    else if (Array.isArray(result?.divisionTimeOff?.[0]?.timeOff)) {
      timeOffEntries = result.divisionTimeOff[0].timeOff;
    }
    // 4. Fallback: flat divisionTimeOff array (some APIs do this)
    else if (Array.isArray(result?.divisionTimeOff)) {
      timeOffEntries = result.divisionTimeOff;
    }

    // If still nothing, try deep search (last resort)
    if (timeOffEntries.length === 0) {
      const findTimeOff = (obj: any): any[] => {
        if (!obj || typeof obj !== 'object') return [];
        if (Array.isArray(obj) && obj.some((item) => item?.day !== undefined)) return obj;
        for (const key in obj) {
          const found = findTimeOff(obj[key]);
          if (found.length) return found;
        }
        return [];
      };
      timeOffEntries = findTimeOff(result);
    }

    // Now apply the entries
    timeOffEntries.forEach((entry: any) => {
      const dayIndex = Number(entry.day ?? entry.dayIndex ?? -1);
      const blockedSlots = Array.isArray(entry.blockedSlots) ? entry.blockedSlots : Array.isArray(entry.slots) ? entry.slots : [];

      if (dayIndex >= 0 && dayIndex < daysCount) {
        blockedSlots.forEach((slotRaw: any) => {
          const slot = Number(slotRaw);
          const lectureIndex = slot - 1;

          if (lectureIndex >= 0 && lectureIndex < lecturesCount) {
            schedule[lectureIndex][dayIndex] = true;
          }
        });
      }
    });
  
    return schedule;
  };
  

  const collectBlockedDays = (schedule: boolean[][], lecturesCount: number, daysCount: number) => {
    const blockedDays: Array<{ day: number; blockedSlots: number[]; reason?: string }> = [];

    for (let dayIndex = 0; dayIndex < daysCount; dayIndex++) {
      const blockedSlots: number[] = [];
      for (let lecture = 0; lecture < lecturesCount; lecture++) {
        if (schedule[lecture]?.[dayIndex]) {
          blockedSlots.push(lecture + 1);
        }
      }
      if (blockedSlots.length > 0) {
        blockedDays.push({
          day: dayIndex,
          blockedSlots,
          reason: 'Time off'
        });
      }
    }

    return blockedDays;
  };

  const resetSubjectSchedule = () => {
    const empty = Array(lecturesCount)
      .fill(null)
      .map(() => Array(daysCount).fill(false));
    setSubjectSchedule(empty);
  };

  const resetDivisionSchedule = () => {
    const empty = Array(lecturesCount)
      .fill(null)
      .map(() => Array(daysCount).fill(false));
    setDivisionSchedule(empty);
  };

  const resetTeacherSchedule = () => {
    const empty = Array(lecturesCount)
      .fill(null)
      .map(() => Array(daysCount).fill(false));
    setTeacherSchedule(empty);
  };

  const buildTimeOffPayload = (schedule: boolean[][]) => {
    const timeOff: { day: number; blockedSlots: number[] }[] = [];
  
    for (let day = 0; day < daysCount; day++) {
      const blockedSlots: number[] = [];
  
      for (let lecture = 0; lecture < lecturesCount; lecture++) {
        if (schedule[lecture]?.[day]) {
          blockedSlots.push(lecture + 1);
        }
      }
  
      if (blockedSlots.length) {
        timeOff.push({ day, blockedSlots });
      }
    }
  
    return timeOff;
  };
  
  const handleAdd = async () => {

    if (!organizationId || !academicYear) {
      Snackbar('Please select Academic Year', 'warning');
      return;
    }

    try {
      if (tabValue === 0) {
        if (!selectedSubject) {
          Snackbar('Please select a Subject', 'warning');
          return;
        }

        const timeOff = buildTimeOffPayload(subjectSchedule);

        // if (!timeOff.length) {
        //   Snackbar('Please select at least one slot', 'warning');
        //   return;
        // }
      
        await subjectTimeoff({
          organizationId,
          subjectId: selectedSubject,
          academicYear,
          timeOff
        });

        Snackbar('Subject time off added successfully', 'success');
      } else if (tabValue === 1) {
        if (!selectedDivision) {
          Snackbar('Please select a Division', 'warning');
          return;
        }
        if (!selectedDivision) {
          Snackbar('Please select a Division', 'warning');
          return;
        }
      
        const timeOff = buildTimeOffPayload(divisionSchedule);
      
        await divisionTimeoff({
          organizationId,
          divisionId: selectedDivision,
          academicYear,
          timeOff
        });

        Snackbar('Division time off added successfully', 'success');
        // resetDivisionSchedule();
      } else if (tabValue === 2) {
        if (!selectedTeacher) {
          Snackbar('Please select a Teacher', 'warning');
          return;
        }

        const timeOff = buildTimeOffPayload(teacherSchedule);

        await teacherTimeoff({
          organizationId,
          teacherId: selectedTeacher,
          academicYear,
          timeOff
        });
      

        Snackbar('Teacher time off added successfully', 'success');
      }
    } catch (err: any) {
      console.error(err);
      Snackbar(err?.response?.data?.message || 'Failed to add time off', 'error');
    }
  };

  const handleCancel = () => {
    if (tabValue === 0) resetSubjectSchedule();
    else if (tabValue === 1) resetDivisionSchedule();
    else if (tabValue === 2) resetTeacherSchedule();
    Snackbar('Changes discarded', 'info');
  };

  useEffect(() => {
    if (tabValue !== 0 || !selectedSubject || !academicYear || !organizationId) {
      resetSubjectSchedule();
      return;
    }

    const fetchSubjectTimeoff = async () => {
      setLoadingSubjectTimeoff(true);
      try {
        const response = await timeoffSubject({
          subjectId: selectedSubject,
          academicYear
        });

        if (response.status === 200 && response.data) {
          const newSchedule = applyScheduleFromApi(response.data, lecturesCount, daysCount);
          setSubjectSchedule(newSchedule);
        } else {
          resetSubjectSchedule();
        }
      } catch (err) {
        console.error('Failed to load subject time off', err);
        Snackbar('Failed to load existing subject time off', 'error');
        resetSubjectSchedule();
      } finally {
        setLoadingSubjectTimeoff(false);
      }
    };

    fetchSubjectTimeoff();
  }, [selectedSubject, academicYear, tabValue, lecturesCount, daysCount]);

  useEffect(() => {
    if (tabValue !== 1 || !selectedDivision || !academicYear ) {
      resetDivisionSchedule();
      return;
    }

    const fetchDivisionTimeoff = async () => {
      setLoadingDivisionTimeoff(true);
      try {
        const response = await timeoffDivision({
          divisionId: selectedDivision,
          academicYear
        });

        if (response.status === 200 && response.data) {
          const newSchedule = applyScheduleFromApi(response.data, lecturesCount, daysCount);
          setDivisionSchedule(newSchedule);
        } else {
          resetDivisionSchedule();
        }
      } catch (err) {
        console.error('Failed to load division time off', err);
        Snackbar('Failed to load existing division time off', 'error');
        resetDivisionSchedule();
      } finally {
        setLoadingDivisionTimeoff(false);
      }
    };

    fetchDivisionTimeoff();
  }, [selectedDivision, academicYear, tabValue, lecturesCount, daysCount]);

  useEffect(() => {
    if (tabValue !== 2 || !selectedTeacher || !academicYear ) {
      resetTeacherSchedule();
      return;
    }

    const fetchTeacherTimeoff = async () => {
      setLoadingTeacherTimeoff(true);
      try {
        const response = await timeoffTeacher({
          teacherId: selectedTeacher,
          academicYear
        });

        if (response.status === 200 && response.data) {
          const newSchedule = applyScheduleFromApi(response.data, lecturesCount, daysCount);
          setTeacherSchedule(newSchedule);
        } else {
          resetTeacherSchedule();
        }
      } catch (err) {
        console.error('Failed to load teacher time off', err);
        Snackbar('Failed to load existing teacher time off', 'error');
        resetTeacherSchedule();
      } finally {
        setLoadingTeacherTimeoff(false);
      }
    };

    fetchTeacherTimeoff();
  }, [selectedTeacher, academicYear, tabValue, lecturesCount, daysCount]);

  useEffect(() => {
    if (!organizationId) return;
    setLoadingSubjects(true);
    getAllSubjects({ organizationId })
      .then((response) => {
        if (response.status === 200 && response.data?.data?.data) {
          setSubjectOptions(
            response.data.data.data.map((subject: any) => ({
              value: subject._id,
              label: subject.subjectName
            }))
          );
        }
      })
      .catch(() => Snackbar('Failed to load subjects', 'error'))
      .finally(() => setLoadingSubjects(false));
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) return;
    setLoadingDivisions(true);
    getAllDivision({ organizationId })
      .then((response) => {
        if (response.status === 200 && response.data?.data?.divisions) {
          const divisions = response.data.data.divisions;

          setDivisionOptions(
            divisions.map((d: any) => ({
              value: d._id,
              label: d.divisionName
            }))
          );

          const years = Array.from(new Set(divisions.map((d: any) => d.academicYear).filter(Boolean)))
            .sort()
            .reverse()
            .map((year) => ({ value: year, label: year }));
          setAcademicYearOptions(years);
          if (years.length === 1) {
            setAcademicYear(years[0].value);
          }
        }
      })
      .catch(() => Snackbar('Failed to load divisions or academic years', 'error'))
      .finally(() => setLoadingDivisions(false));
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) return;
    setLoadingTeachers(true);
    getFilterListUser({
      orgId: organizationId,
      type: 'faculty'
    })
      .then((response) => {
        if (response.status === 200 && response.data?.data) {
          const teachers = response.data.data;
          setTeacherOptions(
            teachers.map((teacher: any) => ({
              value: teacher._id,
              label: `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim() || teacher.email || 'Unnamed Faculty'
            }))
          );
        }
      })
      .catch(() => Snackbar('Failed to load faculty', 'error'))
      .finally(() => setLoadingTeachers(false));
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId || !academicYear) {
      setLecturesCount(6);
      setDaysCount(6);
      setDayLabels(DAYS);
      return;
    }

    getAllDivision({ organizationId })
      .then((response) => {
        if (response.status === 200 && response.data?.data?.divisions) {
          const relevantDivisions = response.data.data.divisions.filter((d: any) => d.academicYear === academicYear);

          if (relevantDivisions.length > 0) {
            const config = relevantDivisions[0];
            const newLectures = config.slotsPerDay || 6;
            const newDays = config.totalDays || 6;

            setLecturesCount(newLectures);
            setDaysCount(newDays);

            const defaultDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
            setDayLabels(defaultDays.slice(0, newDays));
          } else {
            setLecturesCount(6);
            setDaysCount(6);
            setDayLabels(DAYS);
          }
        }
      })
      .catch(() => {
        setLecturesCount(6);
        setDaysCount(6);
        setDayLabels(DAYS);
      });
  }, [academicYear, organizationId]);

  return (
    <MainCard
      title={
        <Box display="flex" alignItems="center">
          <BackToggle show />
          <Box ml={2}>
            <Typography variant="h5">Time Off Management</Typography>
            <Typography variant="body2">Configure unavailable lecture slots for subjects, divisions, and teachers</Typography>
          </Box>
        </Box>
      }
      content={false}
    >
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="time off management tabs"
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons="auto"
          >
            <Tab label="Subject Time Off" {...a11yProps(0)} />
            <Tab label="Division Time Off" {...a11yProps(1)} />
            <Tab label="Teacher Time Off" {...a11yProps(2)} />
          </Tabs>
        </Box>

        {/* Subject Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 3, pb: 0 }}>
            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <InputLabel>
                  Academic Year <RequiredIndicator />
                </InputLabel>
                <FormControl fullWidth>
                  <Select
                    value={academicYear}
                    onChange={(e) => setAcademicYear(e.target.value as string)}
                    displayEmpty
                    disabled={academicYearOptions.length === 0}
                  >
                    <MenuItem value="" disabled>
                      {academicYearOptions.length === 0 ? 'Loading...' : 'Select Year'}
                    </MenuItem>
                    {academicYearOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <InputLabel>
                  Subject <RequiredIndicator />
                </InputLabel>
                <FormControl fullWidth>
                  {loadingSubjects ? (
                    <CircularProgress size={20} sx={{ ml: 2 }} />
                  ) : (
                    <Select value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value as string)} displayEmpty>
                      <MenuItem value="" disabled>
                        Select Subject
                      </MenuItem>
                      {subjectOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </FormControl>
              </Grid2>
            </Grid2>
            <ScheduleGrid
              schedule={subjectSchedule}
              onToggle={handleSubjectToggle}
              lecturesCount={lecturesCount}
              daysCount={daysCount}
              dayLabels={dayLabels}
              loading={loadingSubjectTimeoff}
            />
          </Box>
        </TabPanel>

        {/* Division Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 3, pb: 0 }}>
            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <InputLabel>
                  Academic Year <RequiredIndicator />
                </InputLabel>
                <FormControl fullWidth>
                  <Select
                    value={academicYear}
                    onChange={(e) => setAcademicYear(e.target.value as string)}
                    displayEmpty
                    disabled={academicYearOptions.length === 0}
                  >
                    <MenuItem value="" disabled>
                      {academicYearOptions.length === 0 ? 'Loading...' : 'Select Year'}
                    </MenuItem>
                    {academicYearOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <InputLabel>
                  Division <RequiredIndicator />
                </InputLabel>
                <FormControl fullWidth>
                  {loadingDivisions ? (
                    <CircularProgress size={20} sx={{ ml: 2 }} />
                  ) : (
                    <Select value={selectedDivision} onChange={(e) => setSelectedDivision(e.target.value as string)} displayEmpty>
                      <MenuItem value="" disabled>
                        Select Division
                      </MenuItem>
                      {divisionOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </FormControl>
              </Grid2>
            </Grid2>
            <ScheduleGrid
              schedule={divisionSchedule}
              onToggle={handleDivisionToggle}
              lecturesCount={lecturesCount}
              daysCount={daysCount}
              dayLabels={dayLabels}
              loading={loadingDivisionTimeoff}
            />
          </Box>
        </TabPanel>

        {/* Teacher Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 3, pb: 0 }}>
            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <InputLabel>
                  Academic Year <RequiredIndicator />
                </InputLabel>
                <FormControl fullWidth>
                  <Select
                    value={academicYear}
                    onChange={(e) => setAcademicYear(e.target.value as string)}
                    displayEmpty
                    disabled={academicYearOptions.length === 0}
                  >
                    <MenuItem value="" disabled>
                      {academicYearOptions.length === 0 ? 'Loading...' : 'Select Year'}
                    </MenuItem>
                    {academicYearOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <InputLabel>
                  Teacher <RequiredIndicator />
                </InputLabel>
                <FormControl fullWidth>
                  {loadingTeachers ? (
                    <CircularProgress size={20} sx={{ ml: 2 }} />
                  ) : (
                    <Select value={selectedTeacher} onChange={(e) => setSelectedTeacher(e.target.value as string)} displayEmpty>
                      <MenuItem value="" disabled>
                        Select Faculty
                      </MenuItem>
                      {teacherOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </FormControl>
              </Grid2>
            </Grid2>
            <ScheduleGrid
              schedule={teacherSchedule}
              onToggle={handleTeacherToggle}
              lecturesCount={lecturesCount}
              daysCount={daysCount}
              dayLabels={dayLabels}
              loading={loadingTeacherTimeoff}
            />
          </Box>
        </TabPanel>

        {/* Common Action Buttons */}
        <Box sx={{ px: 3, py: 2 }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" color="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleAdd}>
              Add
            </Button>
          </Stack>
        </Box>
      </Box>
    </MainCard>
  );
}
